import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import { Divider, Typography, Flex } from 'antd';
import styled from 'styled-components';
import { SideWithAsset } from 'shared-types';

Modal.setAppElement('#root');

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '750px',
    width: '90%',
    maxHeight: '80vh',
    padding: '20px',
    border: 'none',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
};

const CloseButton = styled.div`
  text-align: right;
  margin-bottom: 10px;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #000;
  }
`;

const ScrollContent = styled.div`
  max-height: calc(80vh - 100px);
  overflow-y: auto;
`;

const InfoRow = styled(Flex)`
  margin-bottom: 8px;
`;

const Label = styled(Typography.Text)`
  min-width: 240px;
  color: #666;
  font-size: 0.9rem;
`;

const Value = styled(Typography.Text)`
  font-size: 0.9rem;
  font-weight: 500;
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  side: SideWithAsset;
};

export default function PageDetailsModal({ isOpen, onClose, side }: Props) {
  const { t } = useTranslation();
  const { asset, resizeMetadata } = side;
  const { metadata, dimensions } = asset;

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles}>
      <CloseButton onClick={onClose}>
        <CloseOutlined style={{ fontSize: '1.2rem' }} />
      </CloseButton>

      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 20 }}>
        {t('PageDetails.Title')}
      </Typography.Title>

      <ScrollContent>
        {/* File Information */}
        <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0 }}>
          <Typography.Text strong>{t('PageDetails.FileInformation')}</Typography.Text>
        </Divider>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.Filename')}:</Label>
          <Value>{asset.filename}</Value>
        </InfoRow>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.PageNumber')}:</Label>
          <Value>{asset.pageNumber}</Value>
        </InfoRow>

        {/* Dimensions */}
        <Divider orientation="left" orientationMargin={0}>
          <Typography.Text strong>{t('PageDetails.Dimensions')}</Typography.Text>
        </Divider>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.PageSize')}:</Label>
          <Value>
            {metadata.page?.widthMM} × {metadata.page?.heightMM} mm
          </Value>
        </InfoRow>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.PageSizePoints')}:</Label>
          <Value>
            {metadata.page?.width} × {metadata.page?.height} pt
          </Value>
        </InfoRow>

        {/* Boxes */}
        <Divider orientation="left" orientationMargin={0}>
          <Typography.Text strong>{t('PageDetails.Boxes')}</Typography.Text>
        </Divider>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.TrimBox')}:</Label>
          <Value>
            x: {metadata.trimbox?.x}, y: {metadata.trimbox?.y}, w: {metadata.trimbox?.width}, h:{' '}
            {metadata.trimbox?.height}
          </Value>
        </InfoRow>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.CropBox')}:</Label>
          <Value>
            x: {metadata.cropbox?.x}, y: {metadata.cropbox?.y}, w: {metadata.cropbox?.width}, h:{' '}
            {metadata.cropbox?.height}
          </Value>
        </InfoRow>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.BleedBox')}:</Label>
          <Value>
            x: {metadata.bleedbox?.x}, y: {metadata.bleedbox?.y}, w: {metadata.bleedbox?.width}, h:{' '}
            {metadata.bleedbox?.height}
          </Value>
        </InfoRow>

        {/* Colors */}
        {metadata.colors && metadata.colors.length > 0 && (
          <>
            <Divider orientation="left" orientationMargin={0}>
              <Typography.Text strong>{t('PageDetails.Colors')}</Typography.Text>
            </Divider>

            <InfoRow justify="flex-start" align="flex-start">
              <Label>{t('PageDetails.ColorCount')}:</Label>
              <Value>{metadata.colors.length}</Value>
            </InfoRow>

            {metadata.colors.map((color, index) => (
              <InfoRow key={index} justify="flex-start" align="flex-start">
                <Label>
                  {t('PageDetails.Color')} {index + 1}:
                </Label>
                <Flex vertical>
                  <Value>
                    {t('PageDetails.Base')}: {color.base}
                  </Value>
                  {color.color && (
                    <Value>
                      {t('PageDetails.Name')}: {color.color}
                    </Value>
                  )}
                  {color.isCutContour && (
                    <Value style={{ color: '#faad14' }}>{t('PageDetails.IsCutContour')}</Value>
                  )}
                </Flex>
              </InfoRow>
            ))}
          </>
        )}

        {/* Fonts */}
        {metadata.fonts && metadata.fonts.length > 0 && (
          <>
            <Divider orientation="left" orientationMargin={0}>
              <Typography.Text strong>{t('PageDetails.Fonts')}</Typography.Text>
            </Divider>

            {metadata.fonts.map((font, index) => (
              <InfoRow key={index} justify="flex-start" align="flex-start">
                <Label>
                  {t('PageDetails.Font')} {index + 1}:
                </Label>
                <Flex vertical>
                  <Value>{font.name}</Value>
                  <Typography.Text style={{ fontSize: '0.8rem', color: '#999' }}>
                    {t('PageDetails.Type')}: {font.type} | {t('PageDetails.Encoding')}:{' '}
                    {font.encoding}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: '0.8rem', color: '#999' }}>
                    {t('PageDetails.Embedded')}: {font.isEmbedded ? t('Yes') : t('No')} |{' '}
                    {t('PageDetails.Subset')}: {font.isSubSet ? t('Yes') : t('No')}
                  </Typography.Text>
                </Flex>
              </InfoRow>
            ))}
          </>
        )}

        {/* Quality Checks */}
        <Divider orientation="left" orientationMargin={0}>
          <Typography.Text strong>{t('PageDetails.QualityChecks')}</Typography.Text>
        </Divider>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.Resolution')}:</Label>
          <Value>
            {resizeMetadata.lowestResolution === -1
              ? t('PageDetails.NoImages')
              : `${resizeMetadata.lowestResolution} DPI`}
          </Value>
        </InfoRow>

        <InfoRow justify="flex-start" align="center">
          <Label>{t('PageDetails.Scaling')}:</Label>
          <Value>{resizeMetadata.scalingPercentage}%</Value>
        </InfoRow>

        {metadata.checks && (
          <>
            <InfoRow justify="flex-start" align="center">
              <Label>{t('PageDetails.HasRGB')}:</Label>
              <Value>{metadata.checks.hasRGB ? t('Yes') : t('No')}</Value>
            </InfoRow>

            <InfoRow justify="flex-start" align="center">
              <Label>{t('PageDetails.HasCutContour')}:</Label>
              <Value>{metadata.checks.hasCutContour ? t('Yes') : t('No')}</Value>
            </InfoRow>

            <InfoRow justify="flex-start" align="center">
              <Label>{t('PageDetails.HasNotEmbeddedFont')}:</Label>
              <Value>{metadata.checks.hasNotEmbeddedFont ? t('Yes') : t('No')}</Value>
            </InfoRow>

            <InfoRow justify="flex-start" align="center">
              <Label>{t('PageDetails.HasOverprint')}:</Label>
              <Value>{metadata.hasOverprint ? t('Yes') : t('No')}</Value>
            </InfoRow>
          </>
        )}
      </ScrollContent>
    </Modal>
  );
}
